import React, { useMemo } from 'react';
import { Button, Tooltip } from 'antd';
import Accordion from 'components/common/Accordion';
import { useDispatch, useSelector } from 'react-redux';
import * as searchNLPActions from 'actions/searchNLP';
import * as searchSelectors from 'selectors/search';
import * as searchActions from 'actions/search';
import {
  getNLPModalState,
  getPendingAiSessionId,
  getPendingSearchView,
} from 'selectors/searchNLP';
import { GetSearchKeysByView } from 'components/Search/Filters/keys';
import {
  getSearchOrigin,
  ActivitySearch,
  ResearcherSearch,
  OrganisationSearch,
} from 'components/Search/Results/helpers';
import { searchOrigin } from 'components/Search/Results/constants';
import {
  mapAiFiltersToOrgSlice,
  summariseOrgAiFilters,
} from 'components/Organisation/aiFilterAllowlist';
import { applyPresetSideEffects } from 'components/Search/Filters/presetSideEffects';
import { Icon } from 'components/common';
import {
  ReviewQueryBox,
  ReviewAccordionTitle,
  ReviewCountBadge,
  ReviewSection,
  ReviewRow,
  ReviewValues,
  ReviewValueChip,
  ReviewValueChipExcluded,
  ReviewEmptyText,
  IgnoredReviewRow,
  IgnoredValueChip,
  ErrorBox,
  ErrorIcon,
  H3,
  Paragraph,
  StageTwoFooter,
  StageTwoScrollArea,
  FooterConflictWarning,
  AccordionLeadSlot,
  ACCORDION_BODY_INDENT,
} from './styled';
import { aiSynonymSelections } from 'services/api';
import { fireAndForgetPost } from 'services/fireAndForgetPost';
import { buildReviewRows } from './filterReview';
import { buildIgnoredFilterRows } from './orgIgnoredFilterRows';
import { withLabelledSortBy } from './sortByLabel';
import { proximityReviewTooltip } from './chipTooltip';
import { orgIgnoredFilterTooltip } from './strings';
import { NLPSynonymAccordion } from './NLPSynonymAccordion';
import { NLPPluralAccordion } from './NLPPluralAccordion';
import { enrichTermStringWithSynonymsAndPlurals } from './enrichFiltersWithSynonyms';
import { buildDeselectionPayload } from 'reducers/searchNLP/buildDeselectionPayload';
import { buildSelectionPayload } from 'reducers/searchNLP/buildSelectionPayload';
import { sharedAcrossSideLabels } from 'reducers/searchNLP/termSides';
import {
  selectedDistinctLowers,
  dedupSelectionsWithinOrGroups,
} from 'reducers/searchNLP/countDistinctSelections';
import { buildAiFilterFingerprint } from 'utils/aiSavedSearch';
import store from '../../store';
import { colours } from 'utils/theme';
import { useAuth } from 'hooks';
import licenseType from 'models/licenseType';
import { useNlpOrgContext, orgViewForEntityType } from './NlpOrgContext';
import { REQUEST_SOURCE } from './requestSource';

const SYNONYM_LIMIT = 120;

/**
 * Body for POST /api/ai/synonym_selections. Carries requestSource so org-profile
 * selections are not recorded as main search (the server's default). Exported for tests.
 */
export const buildSynonymSelectionsBody = ({
  selectedInclude,
  selectedExclude,
  deselectedInclude,
  deselectedExclude,
  isOrgContext,
}) => ({
  selectedSynonyms: selectedInclude,
  selectedExcludeSynonyms: selectedExclude,
  deselectedSynonyms: deselectedInclude,
  deselectedExcludeSynonyms: deselectedExclude,
  requestSource: isOrgContext
    ? REQUEST_SOURCE.organisationProfile
    : REQUEST_SOURCE.mainSearch,
});

const sectionIcons = {
  Search: 'magnifyingGlassNormal',
  Category: 'filter',
  'Advanced / KOL': 'settings',
  Funding: 'dollar',
  Location: 'map',
  Contacts: 'user',
  Organisation: 'company',
  Tradeshows: 'image',
  'Clinical Trials': 'trial',
  'Venture Capital': 'sackDollar',
  Acquisitions: 'handShake',
  Lists: 'list',
};

const getSectionIcon = sectionTitle => sectionIcons[sectionTitle] || 'filter';

// AI Search can auto-switch the destination view, so the stage-two heading names
// the context (Contact / Organisation / Activity) the search will actually run on
// and shows its matching icon.
const getReviewHeading = effectiveView => {
  if (ResearcherSearch(effectiveView))
    return { icon: 'user', title: 'Contact search terms and filters' };
  if (OrganisationSearch(effectiveView))
    return { icon: 'company', title: 'Organisation search terms and filters' };
  if (ActivitySearch(effectiveView))
    return { icon: 'fileLines', title: 'Activity search terms and filters' };
  return { icon: 'magnifyingGlassNormal', title: 'Search terms and filters' };
};

const extractSortByFromFilters = filters => {
  const { sortBy, ...rest } = filters;
  return [rest, sortBy];
};

const dispatchNewSortByValue = (dispatch, sortByEnumValue, view) => {
  if (!sortByEnumValue) return;
  const actionToDispatch = ResearcherSearch(view)
    ? searchActions.setSortBy
    : searchActions.setOrgSortBy;
  dispatch(actionToDispatch(sortByEnumValue));
};

function useConfirmSearch(onClose) {
  const dispatch = useDispatch();
  const { hasLicense } = useAuth();
  const hasIndustryLicense = hasLicense([licenseType.industry], true);
  const orgCtx = useNlpOrgContext();
  const isOrgContext = Boolean(orgCtx);
  const view = useSelector(searchSelectors.getSearchView);
  const pendingAiSessionId = useSelector(getPendingAiSessionId);
  const pendingSearchView = useSelector(getPendingSearchView);
  const {
    aiSearchInterpretation,
    query,
    synonymsEnabled,
    termEnabled,
    selectedSynonyms,
    pluralsEnabled,
    pluralTermEnabled,
    selectedPluralVariants,
    explicitlySelectedSynonyms,
    termSideMap,
    initiallySelectedSynonyms,
  } = useSelector(getNLPModalState);

  return () => {
    if (!aiSearchInterpretation) {
      return;
    }

    // The org profile switches tab inside runSearchOn and resets its slice
    // atomically inside applyOrgFilters, so the main-search view-switch + reset
    // dispatches below are main-search only.
    if (!isOrgContext && pendingSearchView && pendingSearchView !== view) {
      dispatch(searchActions.setView(pendingSearchView));
    }

    if (!isOrgContext) {
      // SET_FILTERS shallow-merges; reset first so keys omitted from the AI payload do not stick.
      dispatch(searchActions.resetFilters);
    }

    // Extract the sortBy value from the filters and dispatch the appropriate action.
    // sortBy is not part of the org slice contract (it is dropped by
    // mapAiFiltersToOrgSlice), so the org path ignores the extracted enum.
    const [aiSearchFilters, sortByEnumValue] = extractSortByFromFilters(
      aiSearchInterpretation.filters
    );
    if (!isOrgContext) {
      dispatchNewSortByValue(
        dispatch,
        sortByEnumValue,
        pendingSearchView || view
      );
    }

    const effectiveSynonyms = synonymsEnabled
      ? Object.fromEntries(
          Object.entries(selectedSynonyms).filter(([term]) => termEnabled[term])
        )
      : {};

    const effectivePlurals = pluralsEnabled
      ? Object.fromEntries(
          Object.entries(selectedPluralVariants).filter(
            ([term]) => pluralTermEnabled[term]
          )
        )
      : {};

    // The chip-link in the reducer mirrors the same selection across every
    // OR-sibling sub-panel where the chip appears. Without dedup, the enricher
    // emits the same synonym/plural under each sibling and the final Lucene
    // gets `("NGS" OR "illumina") OR ("next gen seq" OR "illumina")`. Attribute
    // each unique label to the first OR-sibling that holds it, drop from the rest.
    const {
      syn: dedupedSynonyms,
      plu: dedupedPlurals,
    } = dedupSelectionsWithinOrGroups(
      effectiveSynonyms,
      effectivePlurals,
      aiSearchInterpretation.orGroups
    );

    const mainSearchView = pendingSearchView || view;
    const isActivityView = ActivitySearch(mainSearchView);
    const aiCategory = aiSearchFilters.category;
    const needsDefaultCategory =
      !aiCategory ||
      typeof aiCategory !== 'object' ||
      !Object.values(aiCategory).includes(true);

    const enrichedFilters = {
      ...aiSearchFilters,
      term: enrichTermStringWithSynonymsAndPlurals(
        aiSearchFilters.term,
        dedupedSynonyms,
        dedupedPlurals
      ),
      // The default-category injection is a main-search "search everything"
      // convenience. The org profile keys categories off its own
      // workItemCategories (mapped from AI `category`); an absent category means
      // "all categories" there already, so we do not inject one.
      ...(!isOrgContext && needsDefaultCategory
        ? {
            category: {
              funding: true,
              tradeshows: true,
              publications: true,
              clinicalTrials: true,
              ...(hasIndustryLicense && !isActivityView
                ? { profile: true }
                : {}),
            },
          }
        : {}),
    };

    if (isOrgContext) {
      // Every narrowing filter was dropped, so running would replace the user's
      // view with an unfiltered one they did not ask for. The modal normally
      // refuses before this step; this guards the paths that reach a review
      // panel without going through filter generation.
      if (summariseOrgAiFilters(enrichedFilters).needsMainSearch) {
        onClose();
        return;
      }
      // Org profile: reset the shared slice, map the enriched AI filters onto it
      // and stamp the session id atomically. The search itself runs last (below),
      // on the tab the classification picked.
      orgCtx.applyOrgFilters(enrichedFilters);
    } else {
      dispatch(
        searchActions.setFilters(
          {
            ...enrichedFilters,
            aiFilterSessionId: pendingAiSessionId,
          },
          false
        )
      );
      dispatch(applyPresetSideEffects(enrichedFilters));
    }

    // The fingerprint pins the AI baseline for drift detection. Off the shared
    // org slice, off the main search slice otherwise — the baseline middleware
    // compares against the matching slice.
    const aiAppliedFingerprint = buildAiFilterFingerprint(
      isOrgContext
        ? store.getState().organisation.filters[orgCtx.organisationId]
        : store.getState().search.filters
    );

    // Atomically promotes the pending session to active + confirmed, stamps provenance,
    // and seeds the conversation. On the org profile only the record is written — the
    // global pointers stay with main search (SCIL-18950).
    dispatch(
      searchNLPActions.sessionActivated({
        queryText: query,
        naturalLanguageQuery: {
          queries: [aiSearchInterpretation.interpretedQuery || query],
        },
        filterResponse: {
          ...aiSearchInterpretation,
          effectiveSynonyms,
          effectivePlurals,
        },
        aiAppliedFingerprint,
        organisationId: isOrgContext ? orgCtx.organisationId : null,
      })
    );

    const {
      include: deselectedInclude,
      exclude: deselectedExclude,
    } = buildDeselectionPayload({
      effectiveSynonyms,
      termEnabled,
      synonymsEnabled,
      termSideMap,
      initiallySelectedSynonyms,
    });

    const {
      include: selectedInclude,
      exclude: selectedExclude,
    } = buildSelectionPayload({
      explicitlySelectedSynonyms,
      effectiveSynonyms,
      termEnabled,
      synonymsEnabled,
      termSideMap,
      initiallySelectedSynonyms,
    });

    if (
      Object.keys(deselectedInclude).length > 0 ||
      Object.keys(deselectedExclude).length > 0 ||
      Object.keys(selectedInclude).length > 0 ||
      Object.keys(selectedExclude).length > 0
    ) {
      fireAndForgetPost(
        aiSynonymSelections,
        buildSynonymSelectionsBody({
          selectedInclude,
          selectedExclude,
          deselectedInclude,
          deselectedExclude,
          isOrgContext,
        })
      );
    }

    // Run the search LAST, after sessionActivated has stamped the session's baseline —
    // so the baseline middleware sees the new session, not a stale one.
    // Main search re-runs via the refreshSearch flag; the org path switches to the
    // classified tab and runs it (the stale mechanism refreshes the other tab on
    // activation).
    if (isOrgContext) {
      orgCtx.runSearchOn(
        pendingSearchView || orgViewForEntityType(orgCtx.entityType)
      );
    } else {
      dispatch(searchActions.setFilter('refreshSearch', true));
    }

    onClose();
  };
}

export function NLPStageTwoModalView({ onClose }) {
  const dispatch = useDispatch();
  const {
    aiSearchInterpretation,
    query,
    synonymsEnabled,
    termEnabled,
    selectedSynonyms,
    pluralsEnabled,
    pluralTermEnabled,
    selectedPluralVariants,
    termSideMap,
  } = useSelector(getNLPModalState);
  const view = useSelector(searchSelectors.getSearchView);
  const pendingSearchView = useSelector(getPendingSearchView);
  const orgCtx = useNlpOrgContext();
  const isOrgContext = orgCtx !== null;
  // On the org profile the tab the modal opened from stands in for the URL view.
  const effectiveView =
    pendingSearchView ||
    (orgCtx ? orgViewForEntityType(orgCtx.entityType) : view);
  const { icon: reviewIcon, title: reviewTitle } = getReviewHeading(
    effectiveView
  );
  const handleConfirm = useConfirmSearch(onClose);

  const filterMeta = useMemo(() => {
    // An org profile searches within one organisation, so its panels expose a
    // different key set from any main-search view, and `getSearchOrigin` has no
    // mapping that yields it. Reading a main-search bag here would both offer
    // filters the profile drops and hide ones it applies.
    const metaOrigin = isOrgContext
      ? searchOrigin.organisationPageSearch
      : getSearchOrigin(effectiveView);
    if (!metaOrigin) {
      return {};
    }

    // Copy: GetSearchKeysByView hands back the shared module-level bag, and the
    // sortBy entry below would otherwise leak into every other consumer of it.
    const keys = { ...(GetSearchKeysByView(metaOrigin) || {}) };

    // We're bundling the sortBy value into the filters object, so we need to add it to the keys object
    keys['sortBy'] = {
      sectionTitle: 'Sort By',
      title: 'Sort By',
      getTitle: () => 'Sort By',
    };
    return keys;
  }, [effectiveView, isOrgContext]);

  // Review what will actually be applied. On the org profile that is the mapped
  // bag, not the raw AI response, so the panel and the applied set are the same
  // thing and unsupported keys cannot reach it.
  const orgMapping = useMemo(
    () =>
      isOrgContext
        ? mapAiFiltersToOrgSlice(aiSearchInterpretation?.filters || {})
        : null,
    [aiSearchInterpretation, isOrgContext]
  );
  const reviewFilters = useMemo(
    () => orgMapping?.filters ?? aiSearchInterpretation?.filters ?? {},
    [orgMapping, aiSearchInterpretation]
  );

  // Named here, before the user commits, rather than in a toast after the
  // results render: this is the last point they can go back and rephrase.
  // Built against the main-search bag, since every dropped key is one the org
  // panels do not expose and so the org bag cannot name.
  const ignoredRows = useMemo(
    () => buildIgnoredFilterRows(orgMapping?.droppedFilters),
    [orgMapping]
  );
  const stageTwoReview = useMemo(() => {
    const review = buildReviewRows(reviewFilters, filterMeta);
    return {
      ...review,
      sections: review.sections.map(section => ({
        ...section,
        rows: withLabelledSortBy(section.rows),
      })),
    };
  }, [reviewFilters, filterMeta]);

  // The 120 cap is about how many OR clauses end up in the final Lucene `term`,
  // so we count DISTINCT selections — collapsing duplicates created by the
  // chip-link across OR-sibling sub-panels — and union synonyms with plurals so
  // a string selected as both contributes only once.
  const distinctExpansionTerms = useMemo(() => {
    const distinctSyn = synonymsEnabled
      ? selectedDistinctLowers(selectedSynonyms, termEnabled)
      : new Set();
    const distinctPlu = pluralsEnabled
      ? selectedDistinctLowers(selectedPluralVariants, pluralTermEnabled)
      : new Set();
    return new Set([...distinctSyn, ...distinctPlu]).size;
  }, [
    synonymsEnabled,
    selectedSynonyms,
    termEnabled,
    pluralsEnabled,
    selectedPluralVariants,
    pluralTermEnabled,
  ]);

  const synonymLimitExceeded = distinctExpansionTerms > SYNONYM_LIMIT;

  // Lowercased labels selected on BOTH an inclusion and an exclusion parent — they
  // cancel out and remove matching results. Shared terms seed off, so this only
  // fills when the user deliberately enables both sides. Passed to the accordions
  // to mark the offending chips amber, and gates the footer warning.
  const bothSideConflictLowers = useMemo(() => {
    const effSyn = synonymsEnabled
      ? Object.fromEntries(
          Object.entries(selectedSynonyms).filter(([term]) => termEnabled[term])
        )
      : {};
    const effPlu = pluralsEnabled
      ? Object.fromEntries(
          Object.entries(selectedPluralVariants).filter(
            ([term]) => pluralTermEnabled[term]
          )
        )
      : {};
    return sharedAcrossSideLabels(termSideMap, effSyn, effPlu);
  }, [
    synonymsEnabled,
    selectedSynonyms,
    termEnabled,
    pluralsEnabled,
    selectedPluralVariants,
    pluralTermEnabled,
    termSideMap,
  ]);

  const handleBack = () => {
    dispatch(
      searchNLPActions.setNLPModalState({
        stage: searchNLPActions.NLP_STAGE.INPUT,
        aiSearchInterpretation: null,
      })
    );
  };

  return (
    <>
      <ReviewQueryBox>
        {aiSearchInterpretation?.interpretedQuery || query}
      </ReviewQueryBox>
      <StageTwoScrollArea>
        <Paragraph>
          From your search these are the filters we think are relevant to your
          search:
        </Paragraph>

        {synonymLimitExceeded && (
          <ErrorBox>
            <ErrorIcon
              icon='error'
              size='sm'
              color={colours.character.warning}
            />
            You've reached your related terms, plural and singular forms limit.
            Please reduce the total number to under {SYNONYM_LIMIT} to continue
            your search.
          </ErrorBox>
        )}

        <Accordion
          isCollapsedByDefault
          bodyIndent={ACCORDION_BODY_INDENT}
          title={
            <div
              style={{
                display: 'flex',
                flex: 1,
                minWidth: 0,
                alignItems: 'center',
                gap: 8,
              }}
            >
              <AccordionLeadSlot>
                <Icon icon={reviewIcon} size='lg' color={null} />
              </AccordionLeadSlot>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  minWidth: 0,
                }}
              >
                <ReviewAccordionTitle>
                  <H3>{reviewTitle}</H3>
                  <ReviewCountBadge>
                    {stageTwoReview.totalCount}
                  </ReviewCountBadge>
                </ReviewAccordionTitle>
                <Paragraph>
                  Review the terms and filters applied to your search based on
                  your initial search
                </Paragraph>
              </div>
            </div>
          }
          testId='nlp-search-review-filters'
        >
          {stageTwoReview.sections.length > 0 ? (
            stageTwoReview.sections.map(section => (
              <ReviewSection key={section.sectionTitle}>
                <H3>
                  <Icon
                    icon={getSectionIcon(section.sectionTitle)}
                    size='xs'
                    color={null}
                  />
                  {section.sectionTitle}
                </H3>
                {section.rows.map(row => (
                  <ReviewRow key={row.key}>
                    <Paragraph>{row.field}:</Paragraph>
                    <ReviewValues>
                      {row.values.map((value, index) => {
                        const isReviewChip =
                          value &&
                          typeof value === 'object' &&
                          typeof value.text === 'string';
                        const text = isReviewChip ? value.text : value;
                        const isExcludedChip = isReviewChip && value.excluded;
                        const Chip = isExcludedChip
                          ? ReviewValueChipExcluded
                          : ReviewValueChip;
                        const key = `${row.key}-${index}`;
                        const chip = <Chip>{text}</Chip>;
                        const tooltip =
                          isReviewChip && value.proximity
                            ? proximityReviewTooltip(
                                value.phrase,
                                value.proximity
                              )
                            : null;
                        return tooltip ? (
                          <Tooltip key={key} title={tooltip}>
                            {chip}
                          </Tooltip>
                        ) : (
                          <React.Fragment key={key}>{chip}</React.Fragment>
                        );
                      })}
                    </ReviewValues>
                  </ReviewRow>
                ))}
              </ReviewSection>
            ))
          ) : (
            <ReviewEmptyText>
              No filters were generated from this query.
            </ReviewEmptyText>
          )}

          {ignoredRows.map(row => (
            // The whole row is the hover target, and antd needs a DOM node to
            // attach to — an Icon component alone never fires the tooltip.
            <Tooltip
              key={`ignored-${row.key}`}
              title={orgIgnoredFilterTooltip}
              placement='right'
            >
              <IgnoredReviewRow data-testid='nlp-org-ignored-filter'>
                <ErrorIcon
                  icon='error'
                  size='sm'
                  color={colours.character.warning}
                />
                <Paragraph>{row.field}:</Paragraph>
                <ReviewValues>
                  {row.values.map((value, index) => {
                    const text =
                      value &&
                      typeof value === 'object' &&
                      typeof value.text === 'string'
                        ? value.text
                        : value;
                    return (
                      <IgnoredValueChip key={`ignored-${row.key}-${index}`}>
                        {text}
                      </IgnoredValueChip>
                    );
                  })}
                </ReviewValues>
              </IgnoredReviewRow>
            </Tooltip>
          ))}
        </Accordion>

        <NLPSynonymAccordion conflictLabels={bothSideConflictLowers} />

        <NLPPluralAccordion conflictLabels={bothSideConflictLowers} />
      </StageTwoScrollArea>

      <StageTwoFooter>
        <Button data-testid='nlp-review-back' onClick={handleBack}>
          Back
        </Button>
        {bothSideConflictLowers.size > 0 && (
          // TODO(SCIL-16099): final warning copy + design pending UX.
          <FooterConflictWarning data-testid='nlp-both-sides-warning'>
            <ErrorIcon
              icon='error'
              size='sm'
              color={colours.character.warning}
            />
            <span>
              Excluding these highlighted terms overrides including them.
            </span>
          </FooterConflictWarning>
        )}
        <Button
          data-testid='nlp-review-confirm'
          type='primary'
          disabled={synonymLimitExceeded}
          onClick={handleConfirm}
        >
          Search
        </Button>
      </StageTwoFooter>
    </>
  );
}
